import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    InputAdornment,
    Box,
    MenuItem,
    FormControl,
    Select,
    Typography
} from '@mui/material';
import { LoadingButton } from "@mui/lab";
import {
    TitleOutlined,
    LocationOnOutlined,
    HouseOutlined,
    StairsOutlined,
    ApartmentOutlined,
    FlagOutlined,
    LocationCity,
    PinDropOutlined,
    LocalPostOfficeOutlined,
    HomeOutlined,
    DescriptionOutlined,
    ErrorOutline
} from "@mui/icons-material";
import { t } from 'i18next';
import { getLocalStorageMenuItem } from '../../utility';



const LocationFormDialog = ({
    open,
    onClose,
    isEditDelivery,
    isProcessLoading,
    locationFormData,
    handleLocationFormInputChange,
    handleSaveLocation,
    handleUpdateLocation,
    setMobileBackColor,
    setMobileTextColor,
    setMobileCardBorderColor,
    setMobileCardColor,
    setMobileButttonBackColor
}) => {

    const LOCATION_TYPES = [
        { value: 'home', label: t("Home1") },
        { value: 'work', label: t("Work") },
        { value: 'friends_house', label: t("Friend's House") },
        { value: 'hangout', label: t("Hangout") }
    ];
    // Check which required fields are empty
    const requiredFields = [
        'street',
        'unit_number',
        'floor_number',
        'apartment_number',
        'country',
        'city',
        'postcode'
    ];

    const emptyFields = requiredFields.filter(
        field => !locationFormData[field] || locationFormData[field].trim() === ''
    );

    return (
        <Dialog
            className="alignByLang"
            disableEscapeKeyDown
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            sx={{
                backgroundColor: setMobileBackColor(),
                color: setMobileTextColor(),
                border: `1px solid ${setMobileCardBorderColor()}`,
            }}
            PaperProps={{
                style: {
                    backgroundColor: setMobileBackColor(),
                    color: setMobileTextColor(),
                    border: `1px solid ${setMobileCardBorderColor()}`,
                },
            }}
        >
            <DialogTitle className="alignByLang head-font">🏠 {t("Enter Location Details")}</DialogTitle>
            <DialogContent dividers className="alignByLang sub-font">
                <Box className="alignByLang" display="flex" flexDirection="column" gap={2} mt={1}>
                    {/* Location Type Dropdown */}
                    <FormControl fullWidth>
                        <Select
                            name="title"
                            value={locationFormData.title || ''}
                            onChange={handleLocationFormInputChange}
                            displayEmpty
                            inputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <TitleOutlined sx={{ color: setMobileTextColor() }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                color: setMobileTextColor(),
                                backgroundColor: setMobileBackColor(),
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: setMobileCardBorderColor(),
                                },
                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: setMobileButttonBackColor(),
                                },
                                "& .MuiSelect-icon": {
                                    color: setMobileTextColor(),
                                },
                            }}
                        >
                            <MenuItem value="" disabled>
                                {t("Select Location Type")}
                            </MenuItem>
                            {LOCATION_TYPES?.map((type) => (
                                <MenuItem key={type.value} value={type.label}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Highlight empty required fields */}
                    {emptyFields.length > 0 && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: 'warning.main',
                            backgroundColor: 'rgba(255, 152, 0, 0.1)',
                            p: 1,
                            borderRadius: 1
                        }}>
                            <ErrorOutline fontSize="small" />
                            <Typography variant="body2">
                                {t("Please complete the highlighted fields")}
                            </Typography>
                        </Box>
                    )}

                    {/* Address Fields */}
                    {requiredFields.map((field) => {
                        const icons = {
                            street: <LocationOnOutlined sx={{ color: setMobileTextColor() }} />,
                            unit_number: <HouseOutlined sx={{ color: setMobileTextColor() }} />,
                            floor_number: <StairsOutlined sx={{ color: setMobileTextColor() }} />,
                            apartment_number: <ApartmentOutlined sx={{ color: setMobileTextColor() }} />,
                            country: <FlagOutlined sx={{ color: setMobileTextColor() }} />,
                            city: <LocationCity sx={{ color: setMobileTextColor() }} />,
                            postcode: <LocalPostOfficeOutlined sx={{ color: setMobileTextColor() }} />,
                        };

                        const labels = {
                            street: t("Street Name"),
                            unit_number: t("Unit No"),
                            floor_number: t("Floor No"),
                            apartment_number: t("Flat No"),
                            country: t("Country"),
                            city: t("City"),
                            postcode: t("Postal Code"),
                        };

                        return (
                            <TextField
                                key={field}
                                name={field}
                                value={locationFormData[field] || ''}
                                onChange={handleLocationFormInputChange}
                                fullWidth
                                placeholder={labels[field]}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {icons[field]}
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    color: setMobileTextColor(),
                                    backgroundColor: setMobileCardColor(),
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: emptyFields.includes(field)
                                            ? 'warning.main'
                                            : setMobileCardBorderColor(),
                                    },
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: setMobileButttonBackColor(),
                                    },
                                    "& .MuiInputBase-input": {
                                        color: setMobileTextColor(),
                                        textAlign: "center",
                                        "&::placeholder": {
                                            color: setMobileTextColor(),
                                            opacity: 0.7,
                                            fontFamily:
                                                getLocalStorageMenuItem("lang") === "ar" ||
                                                    getLocalStorageMenuItem("lang") === "fa"
                                                    ? "Almarai"
                                                    : "Roboto, sans-serif",
                                        },
                                    },
                                }}
                            />
                        );
                    })}

                    {/* Optional Fields */}
                    <TextField
                        name="state"
                        value={locationFormData.state || ''}
                        onChange={handleLocationFormInputChange}
                        fullWidth
                        placeholder={t("State")}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PinDropOutlined sx={{ color: setMobileTextColor() }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            color: setMobileTextColor(),
                            backgroundColor: setMobileCardColor(),
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileCardBorderColor(),
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileButttonBackColor(),
                            },
                            "& .MuiInputBase-input": {
                                color: setMobileTextColor(),
                                textAlign: "center",
                                "&::placeholder": {
                                    color: setMobileTextColor(),
                                    opacity: 0.7,
                                    fontFamily:
                                        getLocalStorageMenuItem("lang") === "ar" ||
                                            getLocalStorageMenuItem("lang") === "fa"
                                            ? "Almarai"
                                            : "Roboto, sans-serif",
                                },
                            },
                        }}
                    />

                    <TextField
                        name="province"
                        value={locationFormData.province || ''}
                        onChange={handleLocationFormInputChange}
                        fullWidth
                        placeholder={t("Province")}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <FlagOutlined sx={{ color: setMobileTextColor() }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            color: setMobileTextColor(),
                            backgroundColor: setMobileCardColor(),
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileCardBorderColor(),
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileButttonBackColor(),
                            },
                            "& .MuiInputBase-input": {
                                color: setMobileTextColor(),
                                textAlign: "center",
                                "&::placeholder": {
                                    color: setMobileTextColor(),
                                    opacity: 0.7,
                                    fontFamily:
                                        getLocalStorageMenuItem("lang") === "ar" ||
                                            getLocalStorageMenuItem("lang") === "fa"
                                            ? "Almarai"
                                            : "Roboto, sans-serif",
                                },
                            },
                        }}
                    />

                    <TextField
                        name="suburb"
                        value={locationFormData.suburb || ''}
                        onChange={handleLocationFormInputChange}
                        fullWidth
                        placeholder={t("Suburb")}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <HomeOutlined sx={{ color: setMobileTextColor() }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            color: setMobileTextColor(),
                            backgroundColor: setMobileCardColor(),
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileCardBorderColor(),
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileButttonBackColor(),
                            },
                            "& .MuiInputBase-input": {
                                color: setMobileTextColor(),
                                textAlign: "center",
                                "&::placeholder": {
                                    color: setMobileTextColor(),
                                    opacity: 0.7,
                                    fontFamily:
                                        getLocalStorageMenuItem("lang") === "ar" ||
                                            getLocalStorageMenuItem("lang") === "fa"
                                            ? "Almarai"
                                            : "Roboto, sans-serif",
                                },
                            },
                        }}
                    />

                    <TextField
                        name="description"
                        value={locationFormData.description || ''}
                        onChange={handleLocationFormInputChange}
                        fullWidth
                        placeholder={t("Address description")}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <DescriptionOutlined sx={{ color: setMobileTextColor() }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            color: setMobileTextColor(),
                            backgroundColor: setMobileCardColor(),
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileCardBorderColor(),
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: setMobileButttonBackColor(),
                            },
                            "& .MuiInputBase-input": {
                                color: setMobileTextColor(),
                                textAlign: "center",
                                "&::placeholder": {
                                    color: setMobileTextColor(),
                                    opacity: 0.7,
                                    fontFamily:
                                        getLocalStorageMenuItem("lang") === "ar" ||
                                            getLocalStorageMenuItem("lang") === "fa"
                                            ? "Almarai"
                                            : "Roboto, sans-serif",
                                },
                            },
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions className="alignByLang" sx={{ flexDirection: "row", justifyContent: "space-around" }}>
                <Button
                    className="head-font"
                    sx={{
                        backgroundColor: setMobileBackColor(),
                        color: setMobileTextColor(),
                        border: `1px solid ${setMobileCardBorderColor()}`,
                        "&.Mui-disabled": {
                            color: setMobileTextColor(),
                        }
                    }}
                    onClick={onClose}
                >
                    {t("Cancel")}
                </Button>
                <LoadingButton
                    className="head-font"
                    sx={{
                        backgroundColor: setMobileBackColor(),
                        color: setMobileTextColor(),
                        border: `1px solid ${setMobileCardBorderColor()}`,
                        "&.Mui-disabled": {
                            color: setMobileTextColor(),
                        }
                    }}
                    onClick={isEditDelivery ? handleUpdateLocation : handleSaveLocation}
                    loading={isProcessLoading}
                    disabled={
                        !locationFormData.title ||
                        emptyFields.length > 0 ||
                        isProcessLoading
                    }
                >
                    {isEditDelivery ? t("Update Location") : t("Save Location")}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default LocationFormDialog;